import React, { FC, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useQuery } from '@apollo/react-hooks';

import { CardTitle } from '../../components/CardTitle';
import { translate } from '../../../../utils/helpers';
import { CardSection } from '../../components/CardSection';
import { AuthSource, LearnerInfo, Query } from '../../../../types';
import AccountDetailsView from '../../components/AccountDetailsView';
import { AccountDetailsForm } from '../../components/AccountDetailsForm';
import { ResetPasswordView } from '../../components/ResetPasswordView';
import { GET_ALL_CATEGORIES } from '../../../../graphql/videos';
import { Loader } from '../../../../components/Loader';
import { SelectOption } from '../const';

interface MyAccountDetailsPageProps {
  userInfo?: LearnerInfo;
  tenantTitle?: string | null;
  authAppId?: string;
}

const MyAccountDetailsPage: FC<MyAccountDetailsPageProps> = ({ userInfo, tenantTitle }) => {
  const [formEnabled, setFormEnabled] = useState(false);

  const { data: categoriesData, loading: loadingCategories } = useQuery<
    Pick<Query, 'listVideoCategories'>
  >(GET_ALL_CATEGORIES);

  const categoriesList: SelectOption[] = useMemo(() => {
    return categoriesData?.listVideoCategories?.length
      ? categoriesData.listVideoCategories
          .filter((item) => !!item?.parent_id)
          .map((item) => ({ value: item?.id, label: item?.title }))
      : [];
  }, [categoriesData]);

  const passwordBasedAuth = userInfo?.auth_source === AuthSource.Auth0;

  if (loadingCategories) {
    return (
      <>
        <Loader />
        <Helmet>
          {translate('page.account.myAccountDetails')}
          {tenantTitle ? ` - ${tenantTitle}` : ''}
        </Helmet>
      </>
    );
  }

  return (
    <>
      <Helmet>
        {translate('page.account.myAccountDetails')}
        {tenantTitle ? ` - ${tenantTitle}` : ''}
      </Helmet>
      <CardTitle title={translate('page.account.myAccountDetails')} />
      <CardSection
        title={translate('page.account.profileInformation')}
        text={[
          translate('page.account.profileInformationDescFirst'),
          translate('page.account.profileInformationDescSecond'),
        ]}
      >
        {formEnabled ? (
          <AccountDetailsForm
            userInfo={userInfo}
            categoriesList={categoriesList}
            setFormEnabled={setFormEnabled}
          />
        ) : (
          <AccountDetailsView userInfo={userInfo} setFormEnabled={setFormEnabled} />
        )}
      </CardSection>
      <CardSection
        title={translate('page.account.security')}
        text={
          passwordBasedAuth
            ? [
                translate('page.account.securityDescFirst'),
                translate('page.account.securityDescSecond'),
              ]
            : [translate('page.account.securityDescFirst')]
        }
      >
        <ResetPasswordView
          email={userInfo?.email}
          passwordBasedAuth={userInfo?.auth_source === AuthSource.Auth0}
        />
      </CardSection>
    </>
  );
};

export default MyAccountDetailsPage;
